import { useContext, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';

import { DynamicUrlCategory } from 'constants/dynamicUrlCategory';
import { PropertyTypeIds } from 'constants/propertyTypes';
import { SortDirections } from 'constants/sortDirections';
import { authContext } from 'contexts/AuthContext';
import { IMarket, ISubmarket } from 'interfaces/IMarket';
import { IDynamicURL } from 'interfaces/IDynamicURL';
import { IPropertySubType } from 'interfaces/IPropertySubType';
import { IPropertyClasses } from 'interfaces/IPropertyClasses';
import { IPropertyTypeResponse } from 'interfaces/IPropertyType';
import { IdName } from 'interfaces/IdName';
import { MARKETS_QUERY, SUB_MARKETS_QUERY } from 'graphql/markets';
import { DYNAMIC_URLS } from 'graphql/dynamicUrl';
import {
  PROPERTY_CLASSES_QUERY,
  PROPERTY_SUBTYPES_QUERY,
  PROPERTY_TYPES_ENTITIES_QUERY,
} from 'graphql/property';
import { ActivitiesSearchService } from 'services/ActivitiesSearchService';
import locations from 'routes';
import { deepCopy } from 'utils/objects';
import { getDefaultPreferencesFilter } from 'utils/preferences';

import { getTopFiveMarketList } from './utils';

interface Props {
  isIndustrialFlex: boolean;
  onCompleteLoadTypes: (types: IdName[]) => void;
}

export const useNavBarData = ({
  isIndustrialFlex,
  onCompleteLoadTypes,
}: Props) => {
  const { user } = useContext(authContext);
  const history = useHistory();

  const [initialListMarket, setInitialListMarket] = useState<IMarket[]>();
  const [currentMarket, setCurrentMarket] = useState<IMarket>();

  const { data: marketsData } = useQuery<{ markets: IMarket[] }>(MARKETS_QUERY);

  /** Markets menu**/
  const { loading: userMarketDataLoading } = useQuery<{ markets: IMarket[] }>(
    MARKETS_QUERY,
    {
      variables: {
        search: {
          ids: user.preferences?.marketIds,
          order: {
            field: 'name',
            direction: SortDirections.asc,
          },
        },
        includePropertiesCount: true,
      },
      onCompleted: d => {
        const listMarkets = getTopFiveMarketList(deepCopy(d?.markets));
        setInitialListMarket(listMarkets);
        setCurrentMarket(listMarkets?.[0]);
      },
    },
  );

  const { data: submarketsData, loading: submarketsDataLoading } = useQuery<{
    submarkets: ISubmarket[];
  }>(SUB_MARKETS_QUERY, {
    variables: {
      search: {
        filter: {
          markets: [{ id: currentMarket?.id }],
          propertyTypeIds: [PropertyTypeIds.office, PropertyTypeIds.industrial],
        },
        order: { field: 'name', direction: SortDirections.asc },
      },
    },

    skip: !currentMarket,
  });

  /** Data menu**/
  const {
    data: propertyTypesData,
    loading: propertyTypesDataLoading,
  } = useQuery<IPropertyTypeResponse>(PROPERTY_TYPES_ENTITIES_QUERY, {
    onCompleted: d => {
      onCompleteLoadTypes(d.propertyTypesEntities);
    },
  });

  const { data: subTypesData, loading: subTypesDataLoading } = useQuery<{
    propertySubtypes: IPropertySubType[];
  }>(PROPERTY_SUBTYPES_QUERY);

  const { data: classesData, loading: classesDataLoading } = useQuery<{
    propertyClasses: IPropertyClasses[];
  }>(PROPERTY_CLASSES_QUERY, { skip: isIndustrialFlex });

  /** Analytics menu**/
  const { data: dynamicUrlsData, loading: dynamicUrlsDataLoading } = useQuery<{
    dynamicURLs: IDynamicURL[];
  }>(DYNAMIC_URLS, {
    variables: {
      search: {
        categories: DynamicUrlCategory.ANALYTICSDASHBOARDS,
      },
    },
  });

  /** Handlers **/
  const handleHoverMarket = (market: IMarket) => {
    if (market.id !== currentMarket?.id) {
      setCurrentMarket(market);
    }
  };

  const handleClickMarket = (market: IMarket) => {
    history.push(locations.showMarket(market.id));
  };

  const onNewsClickHandle = () => {
    return locations.exploreActivity(
      ActivitiesSearchService.parseCustomCriteriaToUrlParams({
        customCriteria: {
          ...getDefaultPreferencesFilter(marketsData?.markets ?? [], user),
        },
      }),
    );
  };

  return {
    data: {
      analytics: dynamicUrlsData?.dynamicURLs ?? [],
      subTypes: subTypesData?.propertySubtypes ?? [],
      classes: classesData?.propertyClasses ?? [],
      propertyTypes: propertyTypesData?.propertyTypesEntities ?? [],
      submarkets: submarketsData?.submarkets ?? [],
      initialListMarket: initialListMarket ?? [],
      currentMarket,
    },
    loading: {
      dynamicUrlsDataLoading,
      propertyTypesDataLoading,
      subTypesDataLoading,
      classesDataLoading,
      userMarketDataLoading,
      submarketsDataLoading,
    },
    handlers: {
      handleHoverMarket,
      handleClickMarket,
      onNewsClickHandle,
    },
  };
};
